import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconAngleDown, IconPenFilled } from '@iwoca/icons';
import { Accordion, IwocaLogo, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { TrustPilot } from '../../../../../../../TrustPilot/TrustPilot';
import { Rate, VariableRate } from '../../../../../../../ui/rate';
import { NeedHelpSection } from '../../../../../components/NeedHelpSection/NeedHelpSection';
import { useLoanSummary } from '../../hooks/useLoanSummary';

type DesktopLoanSummaryProps = {
    formalOffer: TFormalOffer;
    drawdownAmount: number;
};

export const DesktopLoanSummary = ({
    formalOffer,
    drawdownAmount
}: DesktopLoanSummaryProps) => {
    const {
        monthlyRepayment,
        interestRate,
        withdrawalFeePercentage,
        fullTermMonths
    } = useLoanSummary(formalOffer, drawdownAmount);

    return (
        <>
            <div className="flex items-center justify-between">
                <Text className="text-2xl font-bold">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.title"
                        defaultMessage="Your loan summary"
                    />
                </Text>
                <Link
                    to={maintainStateKey({ pathname: '/my-offers/' })}
                    aria-label="Edit loan"
                    className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-structure-100"
                >
                    <IconPenFilled className="h-l w-l" />
                </Link>
            </div>

            <div>
                <Text className="text-m font-reg">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.min_monthly_repayment"
                        defaultMessage="Min. monthly repayment"
                    />
                </Text>
                <Text className="text-m font-med">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.monthly_amount"
                        defaultMessage="{amount} per month"
                        values={{
                            amount: (
                                <span className="text-3xl font-bold">
                                    <FormattedCurrency
                                        value={monthlyRepayment}
                                    />
                                </span>
                            )
                        }}
                    />
                </Text>
            </div>

            <div>
                <div className="flex flex-col gap-m border-t border-secondary-20 pb-2xl pt-xl">
                    <div className="flex items-center justify-between">
                        <Text>
                            <FormattedMessage
                                id="_.loan_summary.withdrawal_amount"
                                defaultMessage="Withdrawal amount:"
                            />
                        </Text>
                        <Text className="font-bold">
                            <FormattedCurrency value={drawdownAmount} />
                        </Text>
                    </div>
                    <div className="flex items-center justify-between">
                        <Text>
                            <FormattedMessage
                                id="_.loan_summary.interest_rate"
                                defaultMessage="Interest rate /30 days:"
                            />
                        </Text>
                        <Text className="font-bold">
                            <Rate value={interestRate} />
                        </Text>
                    </div>
                    <div className="flex items-center justify-between">
                        <Text>
                            <FormattedMessage
                                id="_.loan_summary.withdrawal_fees"
                                defaultMessage="Withdrawal fees:"
                            />
                        </Text>
                        <Text className="font-bold">
                            <VariableRate
                                minimumFractionDigits={0}
                                maximumFractionDigits={2}
                                value={withdrawalFeePercentage}
                            />
                        </Text>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.loan_summary.full_term_duration"
                            defaultMessage="Full term duration:"
                        />
                    </Text>
                    <Text className="font-bold">
                        <FormattedMessage
                            id="_.loan_summary.full_term_months"
                            defaultMessage="{months} months"
                            values={{ months: fullTermMonths }}
                        />
                    </Text>
                </div>
            </div>

            <div className="-ml-m mt-xl">
                <Accordion.Item variant="plain" size="small">
                    <Accordion.Header>
                        <Accordion.Title className="text-s font-bold">
                            <FormattedMessage
                                id="_.funding_requirements.loan_summary.still_need_help"
                                defaultMessage="Still need help?"
                            />
                        </Accordion.Title>
                        <IconAngleDown className="text-l" />
                    </Accordion.Header>
                    <Accordion.Content>
                        <NeedHelpSection variant="funding-desktop" />
                    </Accordion.Content>
                </Accordion.Item>
            </div>

            <div className="mt-auto flex flex-col items-center pt-xl text-center">
                <IwocaLogo className="mb-xl w-[100px]" color="white" />
                <TrustPilot theme="dark" />
            </div>
        </>
    );
};
